/**
 * RackBuilderLiveDashboard Component
 * 
 * Main dashboard component integrating all Live Location Activity features.
 * Displays real-time warehouse activity with automatic rack switching and heatmap visualization.
 * 
 * Features:
 * - Live Mode: Real-time activity animations with automatic rack switching
 * - Heatmap Mode: Historical activity intensity visualization
 * - SignalR WebSocket connection for real-time updates
 * - Queue management with intelligent merging
 * - LRU cache for rack structures (max 10 racks)
 * - Configurable settings (carousel, cache, heatmap, display)
 * - Fullscreen mode support
 * - Activity feed with filtering
 * - Statistics panel with live metrics
 * - Top locations rankings
 * 
 * Layout:
 * ┌─────────────────────────────────────────────────────────┐
 * │ Header: Title │ Connection │ Settings │ Fullscreen     │
 * ├──────┬─────────────────────────────────────┬────────────┤
 * │      │                                     │            │
 * │ Left │      Center (Rack Grid)             │   Right    │
 * │      │  • LiveRackGrid (Live Mode)         │            │
 * │ Mode │  • HeatmapRackGrid (Heatmap Mode)   │ Statistics │
 * │Toggle│                                     │  Activity  │
 * │      ├─────────────────────────────────────┤   Feed     │
 * │      │  Queue Indicator (Live Mode)        │  Top Loc.  │
 * └──────┴─────────────────────────────────────┴────────────┘
 * 
 * @author BizClick WMS
 * @version 1.33 - Fase 9 Implementation (Fixed - Continuous Polling)
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Radio, Settings as SettingsIcon, Maximize, Minimize, ChevronLeft, ChevronRight, AlertCircle, X } from 'lucide-react';
import { Card } from '../../../common/Card';
import { ConnectionStatus } from '../../../common/ConnectionStatus';
import { BackButton } from '../../../common/BackButton';
import { LiveRackGrid } from './LiveRackGrid';
import { HeatmapRackGrid } from './HeatmapRackGrid';
import { ModeToggleCompact } from './ModeToggleCompact';
import { QueueIndicator } from './QueueIndicator';
import { QuickStatsPanel } from './QuickStatsPanel';
import { ActivityStreamPanel } from './ActivityStreamPanel';
import { TopLocationsCompact } from './TopLocationsCompact';
import { SettingsPanel } from './SettingsPanel';
import { ActivityQueueManager } from './ActivityQueueManager';
import { ActivityCacheManager, TimeWindow } from './ActivityCacheManager';
import { RackCarouselController } from './RackCarouselController';
import { HeatmapCalculator } from './HeatmapCalculator';
import { useLocationActivity } from '../../../../hooks/useLocationActivity';
import { DashboardSettingsProvider, useDashboardSettings } from '../../../../contexts/DashboardSettingsContext';
import { LocationActivityEvent, ActivityType, ConnectionStatus as ConnectionStatusEnum } from '../../../../types/locationActivity';
import { RackStructure } from '../rack-builder/types';
import { stcBinService } from '../../../../services/stcBinService';
import { QueueItem } from './types';

interface RackBuilderLiveDashboardProps {
  /** Optional: Callback when user navigates back */
  onBack?: () => void;
}

/**
 * Inner component that uses dashboard settings context
 */
const RackBuilderLiveDashboardInner: React.FC<RackBuilderLiveDashboardProps> = ({ onBack }) => {
  // Settings context
  const { settings } = useDashboardSettings();

  /**
   * Extract rack number from BinCode
   * Tries common rack lengths: 2, 3, 4, 6 characters
   * Falls back to first 2 characters if no better match found
   * 
   * @param binCode The bin code to parse (e.g., "011201", "030205")
   * @returns Rack number as string
   */
  const extractRackNumberFromBinCode = (binCode: string): string => {
    if (!binCode || binCode.length < 2) {
      console.warn(`[Dashboard] Invalid BinCode: ${binCode}`);
      return binCode || 'UNKNOWN';
    }

    // Try common rack lengths in order of likelihood
    const commonRackLengths = [2, 6, 4, 3];
    
    for (const length of commonRackLengths) {
      if (binCode.length >= length) {
        const rack = binCode.substring(0, length);
        // Additional validation: check if remaining length makes sense for vertical+shelf
        const remaining = binCode.length - length;
        // Typical combinations: 2+2, 2+2+2, etc.
        if (remaining % 2 === 0 || remaining % 3 === 0) {
          return rack;
        }
      }
    }

    // Fallback: use first 2 characters
    return binCode.substring(0, 2);
  };

  // UI State
  const [mode, setMode] = useState<'live' | 'heatmap'>('live');
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(settings.heatmap.defaultTimeWindow as TimeWindow);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showStatistics, setShowStatistics] = useState(settings.display.showStatisticsPanel);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Current rack state
  const [currentRack, setCurrentRack] = useState<RackStructure | null>(null);
  const [currentWarehouseId, setCurrentWarehouseId] = useState<string | null>(null);
  const [currentRackNumber, setCurrentRackNumber] = useState<string | null>(null);
  const [currentCompanyNo, setCurrentCompanyNo] = useState<string>('001'); // TODO: Get from user context

  // Active activities tracking (for LiveRackGrid animations)
  const [activeActivities, setActiveActivities] = useState<Map<string, LocationActivityEvent[]>>(new Map());
  const [targetBinCode, setTargetBinCode] = useState<string | null>(null);

  // Refs for managers
  const queueManagerRef = useRef<ActivityQueueManager | null>(null);
  const cacheManagerRef = useRef<ActivityCacheManager | null>(null);
  const carouselControllerRef = useRef<RackCarouselController | null>(null);
  const heatmapCalculatorRef = useRef<HeatmapCalculator | null>(null);
  const processingQueueRef = useRef<boolean>(false);
  const queueProcessorTimerRef = useRef<NodeJS.Timeout | null>(null);
  const processNextInQueueRef = useRef<(() => Promise<void>) | null>(null);

  // Debounce timer for activity cleanup
  const activityCleanupTimerRef = useRef<NodeJS.Timeout | null>(null);

  // SignalR connection
  const {
    connectionStatus,
    connectionQuality,
    lastEvent,
    joinWarehouseGroup
  } = useLocationActivity(
    handleActivityReceived, // onActivityReceived
    handleConnectionStateChanged // onConnectionStateChanged
  );

  /**
   * Initialize managers (memoized)
   */
  const initializeManagers = useCallback(() => {
    console.log('[Dashboard] Initializing managers...');

    // Initialize Queue Manager
    if (!queueManagerRef.current) {
      queueManagerRef.current = new ActivityQueueManager({
        mergeSameRack: settings.carousel.mergeSameRack,
        maxQueueSize: settings.carousel.maxQueueSize,
        dwellTime: settings.carousel.dwellTime,
        enableStats: true
      });
      console.log('[Dashboard] Queue Manager initialized');
    }

    // Initialize Cache Manager
    if (!cacheManagerRef.current) {
      cacheManagerRef.current = new ActivityCacheManager({
        maxCacheSize: settings.cache.maxCacheSize,
        enablePersistence: settings.cache.persistToLocalStorage,
        enableMidnightReset: settings.cache.resetAtMidnight
      });
      console.log('[Dashboard] Cache Manager initialized');
    }

    // Initialize Carousel Controller
    if (!carouselControllerRef.current) {
      const rackLoader = async (warehouseId: string, rackNumber: string): Promise<RackStructure> => {
        console.log(`[Dashboard] Loading rack structure: ${currentCompanyNo}/${warehouseId}/${rackNumber}`);
        const structure = await stcBinService.getRackStructure(currentCompanyNo, warehouseId, rackNumber);
        return structure;
      };

      carouselControllerRef.current = new RackCarouselController(
        rackLoader,
        {
          autoSwitch: settings.display.autoSwitchEnabled,
          dwellTime: settings.carousel.dwellTime,
          smoothTransitions: true,
          transitionDuration: settings.carousel.transitionDuration,
          maxCacheSize: 10,
          preloadNext: true
        }
      );
      
      // Set callbacks
      carouselControllerRef.current.setTransitionCallback((transitionState) => {
        console.log('[Dashboard] Transition:', transitionState.phase);
      });
      
      carouselControllerRef.current.setStateChangeCallback((state) => {
        if (state.currentRack !== null) {
          setCurrentRackNumber(state.currentRack);
          setCurrentWarehouseId(state.currentWarehouseId || null);
        }
      });
      
      console.log('[Dashboard] Carousel Controller initialized');
    }

    // Initialize Heatmap Calculator
    if (!heatmapCalculatorRef.current) {
      heatmapCalculatorRef.current = new HeatmapCalculator({
        timeWindow: TimeWindow.Today,
        normalizationThreshold: 20,
        includeZeroActivity: true
      });
      console.log('[Dashboard] Heatmap Calculator initialized');
    }

    setIsLoading(false);
  }, [settings, currentCompanyNo]);

  /**
   * Initialize managers on mount
   */
  useEffect(() => {
    initializeManagers();

    // Cleanup on unmount
    return () => {
      console.log('[Dashboard] Cleaning up managers...');
      
      if (activityCleanupTimerRef.current) {
        clearTimeout(activityCleanupTimerRef.current);
      }
      
      if (queueProcessorTimerRef.current) {
        clearTimeout(queueProcessorTimerRef.current);
      }

      // Pause carousel
      if (carouselControllerRef.current) {
        carouselControllerRef.current.pause();
      }
    };
  }, [initializeManagers]);

  /**
   * Handle new activity event from SignalR
   */
  function handleActivityReceived(event: LocationActivityEvent): void {
    console.log('[Dashboard] Activity received:', event);

    try {
      // Add to cache
      if (cacheManagerRef.current) {
        cacheManagerRef.current.addEvent(event);
      }

      // Add to queue (if in live mode and auto-switching enabled)
      if (mode === 'live' && settings.display.autoSwitchEnabled && queueManagerRef.current && carouselControllerRef.current) {
        const result = queueManagerRef.current.enqueue(event);
        
        if (result.success) {
          console.log('[Dashboard] Event added to queue:', result.message);
          
          // Check if we should start processing
          // Start if: carousel is paused OR no rack is currently displayed
          const state = carouselControllerRef.current.getState();
          const shouldStartProcessing = (state.isPaused || state.currentRack === null) && !queueManagerRef.current.isEmpty();
          
          if (shouldStartProcessing && processNextInQueueRef.current) {
            console.log('[Dashboard] Starting queue processing (paused:', state.isPaused, ', currentRack:', state.currentRack, ')');
            processNextInQueueRef.current();
          }
        } else {
          console.warn('[Dashboard] Failed to enqueue event:', result.message);
        }
      }

      // Add to active activities for live animations
      if (mode === 'live') {
        setActiveActivities(prev => {
          const updated = new Map(prev);
          const existing = updated.get(event.binCode) || [];
          updated.set(event.binCode, [...existing, event]);
          return updated;
        });

        // Schedule cleanup of this activity
        scheduleActivityCleanup(event);
      }
    } catch (error) {
      console.error('[Dashboard] Error handling activity:', error);
    }
  }

  /**
   * Schedule cleanup of activity after animation completes
   */
  const scheduleActivityCleanup = useCallback((event: LocationActivityEvent) => {
    const animationDuration = settings.display.animationSpeed === 'slow' 
      ? 10000 
      : settings.display.animationSpeed === 'fast'
      ? 4500
      : 7000; // pulse + fadeout

    setTimeout(() => {
      setActiveActivities(prev => {
        const updated = new Map(prev);
        const existing = updated.get(event.binCode) || [];
        const filtered = existing.filter(
          e => !(e.timestamp === event.timestamp && e.activityType === event.activityType)
        );
        
        if (filtered.length > 0) {
          updated.set(event.binCode, filtered);
        } else {
          updated.delete(event.binCode);
        }
        
        return updated;
      });
    }, animationDuration);
  }, [settings.display.animationSpeed]);

  /**
   * Handle connection state changes
   */
  function handleConnectionStateChanged(status: ConnectionStatusEnum): void {
    console.log('[Dashboard] Connection status changed:', status);

    if (status === ConnectionStatusEnum.Connected) {
      setError(null);
      
      // Join warehouse group when connected
      const defaultWarehouseId = 'LAGER01'; // TODO: Get from context
      joinWarehouseGroup(defaultWarehouseId)
        .then(() => {
          console.log('[Dashboard] Joined warehouse group:', defaultWarehouseId);
        })
        .catch(err => {
          console.error('[Dashboard] Failed to join warehouse group:', err);
          setError('Kunne ikke koble til lagergruppe');
        });
    } else if (status === ConnectionStatusEnum.Disconnected) {
      setError('Mistet tilkobling til sanntidsdata. Prøver å koble til på nytt...');
    }
  }

  /**
   * Schedule next queue processing after dwell time
   * This function is called after handleQueueItem completes or when queue is empty
   * It ensures continuous polling of the queue
   */
  const scheduleNextProcessing = useCallback(() => {
    // Only schedule if in live mode and auto-switching is enabled
    if (mode !== 'live' || !settings.display.autoSwitchEnabled) {
      console.log('[Dashboard] Not scheduling - mode:', mode, 'autoSwitch:', settings.display.autoSwitchEnabled);
      return;
    }

    // Clear any existing timer
    if (queueProcessorTimerRef.current) {
      clearTimeout(queueProcessorTimerRef.current);
    }

    console.log('[Dashboard] Scheduling next queue check in', settings.carousel.dwellTime, 'ms');

    // Schedule next queue check after dwell time
    queueProcessorTimerRef.current = setTimeout(() => {
      console.log('[Dashboard] Timer expired - checking queue...');
      
      if (!queueManagerRef.current || !carouselControllerRef.current) {
        console.log('[Dashboard] Managers not ready');
        return;
      }

      // Check if there are items in queue
      if (queueManagerRef.current.isEmpty()) {
        console.log('[Dashboard] Queue is empty, scheduling next check anyway');
        // Schedule next check even if queue is empty - continuous polling
        scheduleNextProcessing();
        return;
      }

      // Process next item
      console.log('[Dashboard] Queue has items, processing...');
      const nextItem = queueManagerRef.current.dequeue();
      if (nextItem && handleQueueItemRef.current) {
        handleQueueItemRef.current(nextItem);
      }
    }, settings.carousel.dwellTime);
  }, [mode, settings.display.autoSwitchEnabled, settings.carousel.dwellTime]);

  // Ref for handleQueueItem to avoid circular dependencies
  const handleQueueItemRef = useRef<((nextItem: QueueItem) => Promise<void>) | null>(null);

  /**
   * Handle a single queue item (extracted from processNextInQueue)
   */
  const handleQueueItem = useCallback(async (nextItem: QueueItem) => {
    if (!carouselControllerRef.current) {
      return;
    }

    processingQueueRef.current = true;

    try {
      // IMPORTANT: Store the target binCode BEFORE switching rack
      // Get first event's binCode as target for scrolling
      const targetBin = nextItem.events.length > 0 ? nextItem.events[0].binCode : null;
      console.log('[Dashboard] Processing queue item for rack:', nextItem.rackNumber, 'target binCode:', targetBin);

      const result = await carouselControllerRef.current.switchToRack(nextItem);

      if (result.success && result.state) {
        console.log('[Dashboard] Switched to rack:', result.message);

        // Update current rack display
        if (result.state.currentRack !== null && result.state.currentWarehouseId) {
          // Load the rack structure manually since switchToRack doesn't return it
          const structure = await stcBinService.getRackStructure(
            currentCompanyNo,
            result.state.currentWarehouseId,
            result.state.currentRack
          );
          setCurrentRack(structure);
          setCurrentRackNumber(result.state.currentRack);
          setCurrentWarehouseId(result.state.currentWarehouseId);

          // IMPORTANT: Add events from queue item to active activities
          // This ensures animations show when rack is displayed
          const newActivities = new Map<string, LocationActivityEvent[]>();
          nextItem.events.forEach(event => {
            const existing = newActivities.get(event.binCode) || [];
            newActivities.set(event.binCode, [...existing, event]);
          });
          setActiveActivities(newActivities);
          console.log('[Dashboard] Set active activities for', newActivities.size, 'locations');

          // Set target binCode for scrolling AFTER rack is loaded (if we have a target)
          if (targetBin) {
            // Wait a bit longer for DOM to be fully rendered
            setTimeout(() => {
              setTargetBinCode(targetBin);
              console.log('[Dashboard] Target binCode set for scrolling:', targetBin);
            }, 100);

            // Reset target binCode after scrolling completes
            setTimeout(() => {
              setTargetBinCode(null);
              console.log('[Dashboard] Target binCode reset after scroll attempt');
            }, 2000);
          }
        }
      } else {
        console.error('[Dashboard] Failed to switch rack:', result.message || 'Unknown error');
        setError(result.message || 'Feil ved bytte av reol');
      }
    } catch (error) {
      console.error('[Dashboard] Error switching rack:', error);
      setError('Feil ved bytte av reol');
    } finally {
      processingQueueRef.current = false;
      
      // IMPORTANT: Schedule next processing after dwell time
      // This ensures continuous polling of queue
      scheduleNextProcessing();
    }
  }, [currentCompanyNo, scheduleNextProcessing]);

  // Update ref when handleQueueItem changes
  useEffect(() => {
    handleQueueItemRef.current = handleQueueItem;
  }, [handleQueueItem]);

  /**
   * Process next item in queue
   */
  const processNextInQueue = useCallback(async () => {
    console.log('[Dashboard] processNextInQueue called');
    if (!queueManagerRef.current || !carouselControllerRef.current) {
      return;
    }
    
    // Prevent concurrent processing
    if (processingQueueRef.current) {
      console.log('[Dashboard] Already processing queue, skipping');
      return;
    }

    const nextItem = queueManagerRef.current.dequeue();
    
    if (nextItem) {
      await handleQueueItem(nextItem);
    } else {
      console.log('[Dashboard] Queue is empty, waiting for new activities');
      // Schedule next check even if queue is empty
      scheduleNextProcessing();
    }
  }, [handleQueueItem, scheduleNextProcessing]);

  // Update ref when processNextInQueue changes
  useEffect(() => {
    processNextInQueueRef.current = processNextInQueue;
  }, [processNextInQueue]);

  /**
   * Start/stop auto-processing based on mode and settings
   */
  useEffect(() => {
    console.log('[Dashboard] Auto-processing effect triggered - mode:', mode, 'autoSwitch:', settings.display.autoSwitchEnabled);
    
    if (mode === 'live' && settings.display.autoSwitchEnabled) {
      console.log('[Dashboard] Starting auto-processing loop');
      // Start the processing loop
      scheduleNextProcessing();
      
      return () => {
        console.log('[Dashboard] Stopping auto-processing loop');
        if (queueProcessorTimerRef.current) {
          clearTimeout(queueProcessorTimerRef.current);
          queueProcessorTimerRef.current = null;
        }
      };
    } else {
      console.log('[Dashboard] Auto-processing disabled');
      // Clear timer if auto-switching is disabled
      if (queueProcessorTimerRef.current) {
        clearTimeout(queueProcessorTimerRef.current);
        queueProcessorTimerRef.current = null;
      }
    }
  }, [mode, settings.display.autoSwitchEnabled, scheduleNextProcessing]);

  /**
   * Handle mode toggle
   */
  const handleModeChange = useCallback((newMode: 'live' | 'heatmap') => {
    console.log('[Dashboard] Mode changed:', newMode);
    setMode(newMode);

    if (newMode === 'live') {
      // Resume carousel if auto-switching enabled
      if (settings.display.autoSwitchEnabled && carouselControllerRef.current) {
        carouselControllerRef.current.resume();
      }
    } else {
      // Pause carousel in heatmap mode
      if (carouselControllerRef.current) {
        carouselControllerRef.current.pause();
      }
    }
  }, [settings.display.autoSwitchEnabled]);

  /**
   * Handle time window change (for heatmap mode)
   */
  const handleTimeWindowChange = useCallback((window: TimeWindow) => {
    console.log('[Dashboard] Time window changed:', window);
    setTimeWindow(window);
  }, []);

  /**
   * Toggle fullscreen mode
   */
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  /**
   * Handle fullscreen change events
   */
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  /**
   * Toggle statistics panel
   */
  const toggleStatistics = useCallback(() => {
    setShowStatistics(prev => !prev);
  }, []);

  /**
   * Get queue stats for display
   */
  const queueStats = useMemo(() => {
    return queueManagerRef.current?.getStats() || {
      queueLength: 0,
      uniqueRacks: 0,
      totalEvents: 0,
      avgEventsPerRack: 0
    };
  }, [queueManagerRef.current?.getStats()]);

  /**
   * Get all cached events for components
   */
  const allCachedEvents = useMemo(() => {
    if (!cacheManagerRef.current) return [];
    return cacheManagerRef.current.getAllEvents();
  }, [cacheManagerRef.current?.getAllEvents()]);

  /**
   * Get recent events for activity feed (last 50)
   */
  const recentEvents = useMemo(() => {
    if (!cacheManagerRef.current) return [];
    
    const allEvents = cacheManagerRef.current.getAllEvents();
    return allEvents.slice(-50).reverse(); // Most recent first
  }, [allCachedEvents]);

  /**
   * Calculate heatmap data
   */
  const heatmapData = useMemo(() => {
    if (mode !== 'heatmap' || !heatmapCalculatorRef.current || !cacheManagerRef.current || !currentRack) {
      return null;
    }

    // Query cache for events within time window
    const queryResult = cacheManagerRef.current.queryByTimeWindow(timeWindow);
    const events = queryResult.events;
    
    // Filter events for current rack
    // Note: event.rackNumber from backend is unreliable, so we extract from BinCode
    const rackEvents = events.filter(event => {
      const rackFromBinCode = extractRackNumberFromBinCode(event.binCode);
      return rackFromBinCode === currentRackNumber;
    });

    // Get all bin codes from current rack
    const allBinCodes = currentRack.locations.map(loc => loc.binCode);

    // Calculate heatmap
    const result = heatmapCalculatorRef.current.calculate(rackEvents, allBinCodes);
    return result;
  }, [mode, timeWindow, currentRack, currentRackNumber, allCachedEvents]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <Radio className="w-16 h-16 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-lg text-gray-700">Laster Live Aktivitet Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-screen bg-gray-50 ${isFullscreen ? 'p-0' : ''}`}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        {/* Left: Title and Connection Status */}
        <div className="flex items-center gap-4">
          {!isFullscreen && <BackButton onClick={onBack || (() => window.history.back())} />}

          <div className="flex items-center gap-3">
            <Radio className="w-7 h-7 text-blue-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Live Aktivitet</h1>
              <p className="text-sm text-gray-600">Sanntidsvisning av lageraktivitet</p>
            </div>
          </div>

          <ConnectionStatus
            status={connectionStatus}
            quality={connectionQuality}
            className="ml-6"
          />
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-3">
          {/* Statistics Panel Toggle */}
          <button
            onClick={toggleStatistics}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
              showStatistics
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            title={showStatistics ? 'Skjul statistikk' : 'Vis statistikk'}
          >
            {showStatistics ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            <span className="text-sm font-medium">Statistikk</span>
          </button>

          {/* Settings Button */}
          <button
            onClick={() => setShowSettings(true)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Innstillinger"
          >
            <SettingsIcon className="w-5 h-5 text-gray-700" />
          </button>

          {/* Fullscreen Toggle */}
          <button
            onClick={toggleFullscreen}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title={isFullscreen ? 'Avslutt fullskjerm' : 'Fullskjerm'}
          >
            {isFullscreen ? (
              <Minimize className="w-5 h-5 text-gray-700" />
            ) : (
              <Maximize className="w-5 h-5 text-gray-700" />
            )}
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Main Content - 3-Column Grid Layout */}
      <div className="flex-1 overflow-hidden bg-gray-100 p-4">
        <div className="grid grid-cols-1 xl:grid-cols-[300px_1fr_380px] 2xl:grid-cols-[320px_1fr_420px] gap-4 h-full overflow-hidden">

          {/* Left Sidebar - Mode Toggle & Top Locations */}
          <aside className="hidden xl:flex flex-col gap-4 overflow-y-auto">
            <ModeToggleCompact
              mode={mode}
              timeWindow={timeWindow}
              onModeChange={handleModeChange}
              onTimeWindowChange={handleTimeWindowChange}
            />

            <TopLocationsCompact
              events={allCachedEvents}
              timeWindow={TimeWindow.Today}
              topCount={5}
              onLocationClick={(binCode) => {
                console.log('[Dashboard] Location clicked:', binCode);
              }}
            />

            {/* Queue Indicator (Live Mode Only) - Moved to bottom of left sidebar */}
            {mode === 'live' && settings.display.showQueueIndicator && carouselControllerRef.current && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
                <QueueIndicator
                  queueStats={queueStats}
                  isAutoSwitching={settings.display.autoSwitchEnabled}
                  isPaused={carouselControllerRef.current.getState().isPaused}
                  onTogglePause={() => {
                    if (carouselControllerRef.current) {
                      const state = carouselControllerRef.current.getState();
                      if (state.isPaused) {
                        carouselControllerRef.current.resume();
                        processNextInQueue();
                      } else {
                        carouselControllerRef.current.pause();
                      }
                    }
                  }}
                />
              </div>
            )}
          </aside>

          {/* Center - Rack Grid (Main Focus) */}
          <main className="flex flex-col overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Rack Grid Container */}
            <div className="flex-1 p-4 overflow-auto">
              {!currentRack ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Radio className="w-16 h-16 text-orange-400 animate-pulse mx-auto mb-4" />
                    <p className="text-lg font-semibold text-gray-700">Venter på aktivitet...</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Reolen vil vises automatisk når aktivitet registreres
                    </p>
                  </div>
                </div>
              ) : mode === 'live' ? (
                <LiveRackGrid
                  rackStructure={currentRack}
                  activeActivities={activeActivities}
                  targetBinCode={targetBinCode}
                  enableTransition={true}
                  animationSpeed={settings.display.animationSpeed}
                />
              ) : (
                <HeatmapRackGrid
                  rackStructure={currentRack}
                  locationIntensities={heatmapData?.locationIntensities || new Map()}
                  targetBinCode={targetBinCode}
                  colorScheme={settings.heatmap.colorScheme}
                />
              )}
            </div>

            {/* Activity Type Legend - Compact Footer */}
            <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                  <span className="text-gray-600">Plukk</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                  <span className="text-gray-600">Plassering</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  <span className="text-gray-600">Mottak</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                  <span className="text-gray-600">Overføring</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-red-500"></span>
                  <span className="text-gray-600">Korreksjon</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-slate-500"></span>
                  <span className="text-gray-600">Telling</span>
                </span>
              </div>
            </div>
          </main>

          {/* Right Sidebar - Statistics & Activity Stream */}
          {showStatistics && (
            <aside className="hidden xl:flex flex-col gap-4 overflow-hidden">
              {/* Quick Stats */}
              <QuickStatsPanel
                allEvents={allCachedEvents}
                queueStats={queueStats}
                currentRack={currentRackNumber}
                currentWarehouseId={currentWarehouseId}
              />

              {/* Activity Stream - Takes remaining space */}
              <div className="flex-1 min-h-0 overflow-hidden">
                <ActivityStreamPanel
                  events={recentEvents}
                  maxEvents={50}
                  autoScroll={true}
                  onEventClick={(event) => {
                    console.log('[Dashboard] Event clicked:', event);
                  }}
                />
              </div>
            </aside>
          )}
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-gray-900">Innstillinger</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>
            </div>
            <div className="p-6">
              <SettingsPanel />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Main component with provider wrapper
 */
export const RackBuilderLiveDashboard: React.FC<RackBuilderLiveDashboardProps> = (props) => {
  return (
    <DashboardSettingsProvider>
      <RackBuilderLiveDashboardInner {...props} />
    </DashboardSettingsProvider>
  );
};

export default RackBuilderLiveDashboard;
